import { sheetSwatches, swatchValue } from "@/lib/stylesheet-typography";
import type { ImageCrop, ImageFilters } from "@/types/content";
import type { GriddStyleSheetData } from "@/types/document";

// Non-destructive image render helpers (TODO §3.3/§3.6). Crop/transform/filters are
// stored as data and applied at render time — nothing is ever re-encoded — so an edit is
// always re-editable. These are pure so the placement math is easy to reason about.

// Build a CSS `filter` string from the stored filter data, or undefined when there's
// nothing to apply. Grayscale/sepia are 0–1; brightness/contrast/saturate are multipliers
// (1 = unchanged). Applied at render (ImageBlock) and for the live editor preview.
export function cssFilter(
  filters: ImageFilters | undefined,
): string | undefined {
  if (!filters) return undefined;
  const parts: string[] = [];
  if (filters.grayscale) parts.push(`grayscale(${filters.grayscale})`);
  if (filters.sepia) parts.push(`sepia(${filters.sepia})`);
  if (filters.brightness != null)
    parts.push(`brightness(${filters.brightness})`);
  if (filters.contrast != null) parts.push(`contrast(${filters.contrast})`);
  if (filters.saturate != null) parts.push(`saturate(${filters.saturate})`);
  return parts.length ? parts.join(" ") : undefined;
}

// Named filter looks the editor offers in a dropdown (TODO §3 filters). Each maps to the
// same ImageFilters data cssFilter() renders, so the picker adds no new render path —
// it just selects a preset. "none" clears filters (stored as undefined on the content).
export interface FilterPreset {
  id: string;
  label: string;
  filters: ImageFilters;
}

export const FILTER_PRESETS: FilterPreset[] = [
  { id: "none", label: "None", filters: {} },
  { id: "grayscale", label: "Grayscale", filters: { grayscale: 1 } },
  {
    id: "noir",
    label: "Noir (B&W)",
    filters: { grayscale: 1, contrast: 1.3, brightness: 0.95 },
  },
  { id: "sepia", label: "Sepia", filters: { sepia: 0.7 } },
  {
    id: "warm",
    label: "Warm",
    filters: { sepia: 0.35, saturate: 1.2, brightness: 1.05 },
  },
  {
    id: "faded",
    label: "Faded",
    filters: { saturate: 0.6, brightness: 1.1, contrast: 0.9 },
  },
  { id: "vivid", label: "Vivid", filters: { saturate: 1.5, contrast: 1.1 } },
  { id: "high-contrast", label: "High contrast", filters: { contrast: 1.4 } },
];

const FILTER_KEYS = [
  "grayscale",
  "sepia",
  "brightness",
  "contrast",
  "saturate",
] as const;

// True when two filter sets are equal, treating missing and undefined alike — so the
// editor can reverse-map a content record's stored filters back to a preset id.
export function sameFilters(
  a: ImageFilters | undefined,
  b: ImageFilters | undefined,
): boolean {
  return FILTER_KEYS.every(
    (k) => (a?.[k] ?? undefined) === (b?.[k] ?? undefined),
  );
}

// The preset id matching the given filters, or "none" when nothing matches (all presets
// here come from the picker, so this always resolves to one of them in practice).
export function filterPresetId(filters: ImageFilters | undefined): string {
  return (
    FILTER_PRESETS.find((p) => sameFilters(p.filters, filters))?.id ?? "none"
  );
}

// How an image is laid into its content window (B4). `cover` scales until the crop fills the
// window and clips whatever overflows; `contain` fits the whole crop inside it and letterboxes
// the remainder. Before B4 there was no such choice — every raster image was `cover`, so an
// untouched diagram was silently scaled until it overflowed and the overflow was cut off.
export type ImageFit = "contain" | "cover";

// The fit an image record actually renders with, and the ONLY place that decision is made
// (B4 fix, part 1). A stored crop rectangle is an explicit statement that *this rectangle is
// the picture*, so filling the window with it is right; an image with no crop was never
// cropped by anybody, and the app must not invent a crop for it.
//
// §22.1 makes the fit user-settable by putting `content.fit ??` in front of this expression —
// one line, here, because every render path reads the fit through this function.
export function effectiveFit(content: { crop?: ImageCrop }): ImageFit {
  return content.crop ? "cover" : "contain";
}

// === The window's background (§22.2) ===

// What an image window paints behind the picture when it has no colour of its own. White,
// because that is what every sibling block paints (`DiagramBlock`, `GanttBlock`, `ChartBlock`
// are all `bg-white`) and what the slide canvas under them is — so an uncropped image
// letterboxes into the slide instead of into a grey card that nobody chose. The old
// `bg-slate-100` here and `#f8fafc` in the HTML export were two different greys, which is the
// other half of why this constant exists: one default, read by both paths.
export const IMAGE_BACKGROUND_DEFAULT = "#ffffff";

// The colour an image window actually paints, and the ONLY place that decision is made —
// `effectiveFit`'s counterpart, and for the same reason: the editor, presentation, print, the
// thumbnail capturer and the HTML export all render through one of the two call sites, so
// they cannot disagree about what shows in the letterbox.
//
// The stored value is a NAME (§6.2.2). A name the sheet no longer defines resolves to the
// default rather than to the literal string, which would paint nothing at all.
export function imageBackground(
  styleSheet: GriddStyleSheetData,
  content: { background?: string },
): string {
  return (
    swatchValue(sheetSwatches(styleSheet), content.background) ??
    IMAGE_BACKGROUND_DEFAULT
  );
}

// Where the image and its crop rectangle land inside the content window, in window px.
export interface ImagePlacement {
  // The crop rectangle's rendered box within the window. It CLIPS the image, so pixels
  // outside the crop are never visible — which is what lets `contain` letterbox a cropped
  // image without leaking the parts the crop was meant to remove. Under `cover` this box is
  // larger than the window in at least one axis and the window's own overflow trims it.
  clip: { left: number; top: number; width: number; height: number };
  // The whole image's box, positioned relative to the clip box.
  image: { left: number; top: number; width: number; height: number };
}

// Place an image's crop rectangle inside a box under the given fit, centred on BOTH axes.
// Returns null until sizes are known.
//
// The two fits differ in exactly one term — `max` fills, `min` fits — and share the centring,
// which is the point of having one function: the editor, presentation, print and the
// thumbnail capturer cannot drift apart because there is nothing to keep in step.
//
// **Do not reintroduce a `Math.max(left, 0)` clamp here** (B4 defect (b)). One lived on `left`
// and not on `top`, and after the bounds clamp below it could only ever return 0: `c.x >= 0`
// and `scale >= bw / c.width`, so `c.width * scale >= bw` and the whole expression is `<= 0`
// for every input. It silently disabled horizontal centring — the image was pinned to the left
// edge and every lost pixel came off the right, worsening as the window narrowed — while the
// unclamped vertical axis centred correctly, which is why the two axes visibly disagreed. The
// out-of-bounds overrun its comment described is handled by the crop clamp, not by the clamp
// on the offset.
export function placeImage(
  crop: ImageCrop | undefined,
  natural: { width: number; height: number },
  box: { width: number; height: number },
  fit: ImageFit,
): ImagePlacement | null {
  const { width: NW, height: NH } = natural;
  const { width: bw, height: bh } = box;
  if (!NW || !NH || !bw || !bh) return null;

  // Clamp the crop to the image's real bounds. The editor's `restrictPosition={false}` lets a
  // stored crop spill past the image into empty space; laid out verbatim, that fills the box
  // with pixels that don't exist — so the actual image comes up short and shifted off-centre.
  // Intersecting with [0..NW]x[0..NH] means we only ever place real pixels; a fully in-bounds
  // crop is unchanged.
  const raw = crop ?? { x: 0, y: 0, width: NW, height: NH };
  const cx = Math.max(0, raw.x);
  const cy = Math.max(0, raw.y);
  const c = {
    x: cx,
    y: cy,
    width: Math.min(NW, raw.x + raw.width) - cx,
    height: Math.min(NH, raw.y + raw.height) - cy,
  };
  if (!(c.width > 0) || !(c.height > 0)) return null;

  const scale =
    fit === "cover"
      ? Math.max(bw / c.width, bh / c.height)
      : Math.min(bw / c.width, bh / c.height);
  const cropW = c.width * scale;
  const cropH = c.height * scale;

  return {
    clip: {
      left: (bw - cropW) / 2,
      top: (bh - cropH) / 2,
      width: cropW,
      height: cropH,
    },
    image: {
      left: -c.x * scale,
      top: -c.y * scale,
      width: NW * scale,
      height: NH * scale,
    },
  };
}

// A quarter-turn swaps the box's width/height, so the placement is computed against the
// *pre-rotation* box and the whole stage is then rotated to fill the container.
export function isQuarterTurn(rotation: number | undefined): boolean {
  return rotation === 90 || rotation === 270;
}
